import React, { useEffect, useState } from "react";
import { FaEdit, FaTrashAlt } from "react-icons/fa";
import ListHeader from "../../../components/listing/ListHeader";
import ListFooter from "../../../components/listing/ListFooter";
import FiltroProducto from "./FiltroProducto";
import CrearProducto from "./CrearProducto";
import EditarProducto from "./EditarProducto";
import EliminarProducto from "./EliminarProducto";
import DynamicTable from "../../../components/listing/DynamicTable";
import apiRoutes from "../../../api/apiRoutes";
import { appFetchJson } from "../../../utils/http";

export default function ListarProductos() {
  const [porPagina, setPorPagina] = useState(30);
  const [pagina, setPagina] = useState(1);
  const [totalBackend, setTotalBackend] = useState(0);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [mostrarFiltros, setMostrarFiltros] = useState(false);
  const [mostrarCrear, setMostrarCrear] = useState(false);
  const [productoAEditar, setProductoAEditar] = useState(null);
  const [productoAEliminar, setProductoAEliminar] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [filtros, setFiltros] = useState({ codigo: "", nombre: "", estadoId: null, rubroId: null, ivaId: null, codigoSap: "" });
  
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true); setError(null);
      try {
        let baseUrl = apiRoutes.productos.listUrl({}); 
        if (baseUrl.includes('?')) baseUrl = baseUrl.split('?')[0];
        const queryParams = new URLSearchParams();
        queryParams.append("page", pagina - 1);
        queryParams.append("size", porPagina);
        if (filtros.nombre) queryParams.append("nombre", filtros.nombre.trim());
        if (filtros.codigo) queryParams.append("codigo", filtros.codigo.trim());
        if (filtros.codigoSap) queryParams.append("codigoSap", filtros.codigoSap.trim());
        if (filtros.estadoId) queryParams.append("estadoId", filtros.estadoId);
        if (filtros.rubroId) queryParams.append("rubroId", filtros.rubroId);
        if (filtros.ivaId) queryParams.append("ivaId", filtros.ivaId);
        const url = `${baseUrl}?${queryParams.toString()}`;
        const res = await appFetchJson(url);
        const items = Array.isArray(res?.items) ? res.items : [];
        setData(items);
        const total = res?.totalElements ?? res?.total ?? (Array.isArray(items) ? items.length : 0);
        setTotalBackend(total);
      } catch (e) {
        console.error("Error cargando productos:", e);
        setError("Error al cargar los productos.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [pagina, porPagina, filtros, reloadKey]);

// ====================================================================
  // DEFINICIÓN DE COLUMNAS (SIMETRÍA SAP - IVA - RUBRO)
  // ====================================================================
  const columns = [
    
    { header: "Cód.", 
      accessor: "codigo", 
      type: "center", 
      width: "9%", 
      cellClass: "font-mono font-bold text-gray-600" }, //Los códigos (4201092, 0111840) son identificadores numéricos. 
                                                       // Al usar font-mono, logramos que los números queden perfectamente alineados uno debajo del otro, 
                                                       // como en una columna de Excel
    
    { 
      header: "Descripción", 
      accessor: "nombre", 
      type: "text", 
      width: "18%", 
      cellClass: "whitespace-normal break-words leading-tight font-medium" //Adáptate al espacio que tengas (whitespace-normal), 
                                                                          //si no cabes córtate (break-words), y no ocupes demasiado espacio vertical (leading-tight)
    }, 
    
    { header: "Cód. SAP", 
      accessor: "codigoSap", 
      type: "code_wide", 
      width: "9%"}, 
    
    { header: "IVA", 
      accessor: "ivaNombre", 
      type: "secondary", 
      width: "10%"}, 
    
    { 
      header: "Rubro", 
      accessor: "rubroNombre", 
      type: "text", 
      width: "10%", 
      cellClass: "text-gray-600 whitespace-normal break-words leading-tight font-medium" //Adáptate al espacio que tengas (whitespace-normal), 
                                                                                        //si no cabes córtate (break-words), y no ocupes demasiado espacio vertical
    }, 
    
    { header: "Unidad Medida", 
      type: "center", 
      width: "7%", 
      render: (it) => it.unidadMedidaNombre || "-" }, 
    
    { 
      header: "Estado", 
      type: "status", 
      width: "11%",
      render: (it) => {
        const desc = it.estadoDescripcion?.toUpperCase() || "";
        const esActivo = desc.includes('ACTIVO') && !desc.includes('INACTIVO');
        const textColor = esActivo ? 'text-green-600' : 'text-red-600';
        return <span className={`font-bold ${textColor}`}>{it.estadoDescripcion}</span>;
      }
    }, 
    
    { header: "Precio Portón", 
      type: "center",
       width: "9%", 
       render: (it) => it.precioPorton ? it.precioPorton.toFixed(2) : "-" },

    { header: "Precio Compra", 
      type: "center", 
      width: "9%", 
      render: (it) => it.precioCompra ? it.precioCompra.toFixed(2) : "-" },
    
    {
      header: "Acción",
      type: "action", 
      width: "7%",
      render: (it) => (
        <div className="flex justify-center items-center gap-1">
          <button className="text-gray-500 hover:text-blue-600 p-1" onClick={() => setProductoAEditar(it)}><FaEdit /></button>
          <button className="text-gray-500 hover:text-red-600 p-1" onClick={() => setProductoAEliminar(it)}><FaTrashAlt /></button>
        </div>
      )
    }
  ];

  const getRowBorderColor = (row) => {
    const desc = row.estadoDescripcion?.toUpperCase() || "";
    const esActivo = desc.includes('ACTIVO') && !desc.includes('INACTIVO');
    return esActivo ? 'border-green-600' : 'border-red-600';
  };

  return (
    <div className="page">
      <ListHeader
        title="Productos"
        filters={filtros}
        onFilterClick={() => setMostrarFiltros(true)}
        onClearFilters={() => { setFiltros({ codigo: "", nombre: "", estadoId: null, rubroId: null, ivaId: null, codigoSap: "" }); setPagina(1); }}
        showAdd={true} addLabel="Agregar" onAddClick={() => setMostrarCrear(true)}
        pageSize={porPagina} onPageSizeChange={(size) => { setPorPagina(size); setPagina(1); }}
      />
      {loading && <div className="mt-2 text-sm text-gray-600">Cargando datos...</div>}
      {error && <div className="mt-2 text-sm text-red-600">{error}</div>}
      {mostrarFiltros && (
        <FiltroProducto
          initialFilters={filtros}
          onSubmit={(nuevosFiltros) => { setFiltros(nuevosFiltros); setPagina(1); setMostrarFiltros(false); }}
          onClose={() => setMostrarFiltros(false)}
        />
      )}
      
      <DynamicTable 
        size="L" 
        data={data}
        columns={columns}
        getRowColor={getRowBorderColor}
      />
      
      <ListFooter page={pagina} pageSize={porPagina} total={totalBackend} onPageChange={(p) => setPagina(p)} />
      {mostrarCrear && (
  <CrearProducto
    onClose={() => setMostrarCrear(false)}
    onCreated={() => {
      setReloadKey((k) => k + 1);
      setMostrarCrear(false);
    }}
  />
)}

{productoAEditar && (
  <EditarProducto
    initial={productoAEditar}
    onClose={() => setProductoAEditar(null)}
    onSaved={() => {
      setReloadKey((k) => k + 1);
      setProductoAEditar(null);
    }}
  />
)}

{productoAEliminar && (
  <div className="modal-backdrop" onClick={() => setProductoAEliminar(null)}>
    <div className="modal-panel-md" onClick={(e) => e.stopPropagation()}>
      <EliminarProducto
        registro={productoAEliminar}
        onClose={() => setProductoAEliminar(null)}
        onDeleted={() => {
          setReloadKey((k) => k + 1);
          setProductoAEliminar(null);
        }}
      />
    </div>
  </div>
)}
    </div>
  );
}